import { Injectable, NgZone } from '@angular/core';
import { DocumentImagePostType, PropertiesService } from '@barthauer/abc/common-editors';
import { AbcCore, ConvertHelper, GUID } from '@barthauer/abc/core';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import {
  ICoreDataEntity,
  IPersistable,
  ObjectKey,
  ObjectTypeInfoViewModel,
  PersistenceService,
  PersistMode,
  PersistObjectModel,
  Query,
  QueryColumn,
  QueryObjectsModel,
  TargetColumnValue,
  TargetObjectData,
} from '@barthauer/abc/core/data';
import { MessageBoxService } from '@barthauer/abc/messages';
import { AbcContainerService, AbcCoreContainerService, _AbcGridComponent } from '@barthauer/abc/ui-components';
import { AbcGridContextMenuService } from 'libs/abc/ui-components/src/lib/base/src/abcGrid/abcGridContextMenu.service';
import { lastValueFrom, Subject } from 'rxjs';
import { AbcArenaService } from '../../abc-arena.service';
import { DataService } from '../../services/data.service';
import { ArenaService } from '../arena.service';
import { LayoutColumnViewModel, SubLayoutViewModel } from '../viewModels/layout.viewModel';
import { Document } from '../models/layout.model';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';

declare let navigator: any;
declare let Camera: any;

@Injectable({
  providedIn: 'root',
})
export class DataFormService {
  private dataFormFieldsSubject = new Subject<any>();

  public applicationTitle;
  public cdrSubject: Subject<void> = new Subject();
  public crd$ = this.cdrSubject.asObservable();
  public documentImages: DocumentImagePostType[] = [];
  public emptyDataFormFields$ = this.dataFormFieldsSubject.asObservable();
  public readonly DISPLAYTEXT_PREFIX = '__DISPLAY_TEXT__';
  public readonly LIST_END_KEY = ['End', 'End'];
  public readonly LIST_START_KEY = ['Start', 'Begin'];

  constructor(
    public abcArena: AbcArenaService,
    public abcContainer: AbcContainerService,
    public abcCoreContainer: AbcCoreContainerService,
    public abcContextMenu: AbcGridContextMenuService,
    public arena: ArenaService,
    public core: AbcCore,
    public config: AbcConfigService,
    public data: DataService,
    public messageBox: MessageBoxService,
    public ngZone: NgZone,
    public persistence: PersistenceService,
    public properties: PropertiesService
  ) {}

  public createGridOnFormObjectsQuery(
    parameters: ParameterValue[],
    objectType,
    subLayoutView: SubLayoutViewModel
  ): QueryObjectsModel {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const gridOnFormObjectsQuery: Query = new Query();
    gridOnFormObjectsQuery.ObjectType = objectType;
    gridOnFormObjectsQuery.OPath = 'Type(' + objectType + ')[Id=$Id].' + subLayoutView.opath;
    gridOnFormObjectsQuery.Columns = this.prepareGridOnFormColumns(subLayoutView.columns);
    queryDocument.ObjectQueries = [gridOnFormObjectsQuery];
    queryDocument.Parameters = parameters;
    return queryDocument;
  }

  public createGridOnFormPersistQuery(
    gridOnFormObject: IGridOnForm,
    mode: PersistMode
    // columnsObjectMap?: Map<MinimizedLayoutColumn, ColumnObject>
  ) {
    const columns = gridOnFormObject.abcGrid?.columns;
    const result: PersistObjectModel = new PersistObjectModel();
    const gridOnFormPersistQuery: TargetObjectData = new TargetObjectData();
    gridOnFormPersistQuery.Mode = mode;
    gridOnFormPersistQuery.ObjectKey = new ObjectKey();

    if (gridOnFormObject.data.Id) {
      gridOnFormPersistQuery.ObjectKey.Id = gridOnFormObject.data.Id;
    }
    gridOnFormPersistQuery.ObjectKey.ObjectType = gridOnFormObject.data.ObjectType;
    const targetColumns: TargetColumnValue[] = [];

    if (mode === PersistMode.Insert || mode === PersistMode.Update) {
      for (const [key, value] of Object.entries(gridOnFormObject.data)) {
        for (const column of columns) {
          if (typeof column !== 'string') {
            if (column.dataField === key) {
              const opath = column.name.split('|')[1];

              if (key !== 'Id' && key !== 'ObjectType' && !key.startsWith(this.DISPLAYTEXT_PREFIX)) {
                // if (value != null) {
                targetColumns.push({
                  Name: opath,
                  Value: value,
                });
                // }
              }
              break;
            }
          }
        }
      }

      if (gridOnFormPersistQuery.Mode === PersistMode.Insert) {
        gridOnFormPersistQuery.ObjectKey.ObjectType = gridOnFormObject.objectType;
        if (!targetColumns.find(_ => _.Name === gridOnFormObject.referencingIdColumn)) {
          targetColumns.push({
            Name: gridOnFormObject.referencingIdColumn,
            Value: gridOnFormObject.mainObject?.Id,
          });
        }
        if (gridOnFormObject.referencingObjectTypeColumn) {
          if (!targetColumns.find(_ => _.Name === gridOnFormObject.referencingObjectTypeColumn)){
          targetColumns.push({
            Name: gridOnFormObject.referencingObjectTypeColumn,
            Value: gridOnFormObject.mainObject?.ObjectType,
          });}
        }
      }
      gridOnFormPersistQuery.TargetColumns = targetColumns;
    }
    result.Object = gridOnFormPersistQuery;
    return result;
  }

  public callEmptyDataFormFields(): void {
    this.dataFormFieldsSubject.next(null);
  }

  private prepareGridOnFormColumns(columns: LayoutColumnViewModel[]): QueryColumn[] {
    const gridOnFormColumns: QueryColumn[] = [];

    if (columns) {
      for (let i = 0; i < columns.length; i++) {
        if (columns[i].oPath) {
          if (columns[i].oPath.startsWith('ParentId') || columns[i].oPath.startsWith('IdRef') || columns[i].isRef) {
            gridOnFormColumns.push({
              Name: this.DISPLAYTEXT_PREFIX + columns[i].dataField,
              OPath: columns[i].oPath,
            });
          }

          gridOnFormColumns.push({
            Name: columns[i].dataField || columns[i].caption,
            OPath: columns[i].oPath.startsWith('ref:') ? columns[i].oPath?.substring('ref:'.length) : columns[i].oPath,
          });
        }
      }

      gridOnFormColumns.push(
        {
          Name: 'Id',
          OPath: 'Id',
        },
        {
          Name: 'ObjectType',
          OPath: 'ObjectType',
        }
      );
    }
    return gridOnFormColumns;
  }

  // It will be changed (temporary) ( for image Viewer)

  takePicture(cameraCallback, useGalery: boolean) {
    const options = {
      quality: 100,
      destinationType: Camera.DestinationType.DATA_URL,
      sourceType: useGalery ? Camera.PictureSourceType.SAVEDPHOTOALBUM : Camera.PictureSourceType.CAMERA,
      encodingType: Camera.EncodingType.JPEG,
      mediaType: Camera.MediaType.PICTURE,
      allowEdit: false,
      correctOrientation: true,
    };

    navigator.camera.getPicture(
      (imageUri) => cameraCallback(imageUri),
      (error) => alert('Fehler beim Zugriff auf die Kamera. Details: ' + error),
      options
    );
  }

  public async getDocumentsByKind(parentId: string, kind: string): Promise<Document[]> {
    const query: QueryObjectsModel = new QueryObjectsModel();
    const documentsQuery: Query = new Query();
    documentsQuery.ObjectType = 'Document';
    documentsQuery.OPath = `ParentId = '${parentId}' And Kind = '${kind}'`;
    documentsQuery.Columns = [
      { Name: 'Id', OPath: 'Id' },
      { Name: 'Object', OPath: 'Object' },
      { Name: 'Designation', OPath: 'Designation' },
      { Name: 'Date', OPath: 'Date' },
    ];
    query.ObjectQueries = [documentsQuery];

    return ConvertHelper.arrayBufferToObjectArray<Document>(
      await lastValueFrom(this.core.data.executeReadObjectsQuery(query))
    );
  }

  public persistDocumentImages() {
    this.arena.persistDocumentImagesSubject.subscribe((parentId) => {
      //TODO: At least use specific persistenceCategory!
      this.persistence.clearPendingObjects();
      for (let image of this.documentImages) {
        let id = GUID.newGUID();
        const objectKey: IPersistable = {
          id: id,
          objectType: 'Document',
          persistenceCategory: null,
          changesPersisted: (persistMode) => (persistMode = PersistMode.Insert),
          revertChanges: () => {},
        };
        this.persistence.clearPendingObjects();
        this.persistence.addObjectForInsert(objectKey);
        this.persistence.addObjectValue(objectKey, 'Designation', image.Designation);
        this.persistence.addObjectValue(objectKey, 'Kind', image.Kind);
        this.persistence.addObjectValue(objectKey, 'Object', image.Object);
        this.persistence.addObjectValue(objectKey, 'Date', image.Date);
        this.persistence.addObjectValue(objectKey, 'ParentId', parentId);
        this.persistence.addObjectValue(objectKey, 'ParentObjectType', image.ParentObjectType);
        this.persistence.persistPendingChanges();
      }
    });
  }

  public prepareInitializationColumnQuery(referencedObjectType: ObjectTypeInfoViewModel, id: string) {
    const column = new QueryColumn();
    column.Name = 'DisplayText';
    column.OPath = 'ref:Id';

    let query = this.core.data.createSimpleQuery('', referencedObjectType.name, 'Id=' + "'" + id + "'", [column]);

    return query;
  }

  public prepareInitialDropDownText(initialValue: string, initialDisplayText: string): string {
    if (!initialDisplayText) {
      initialDisplayText = `<<${initialValue}>>`;
    }
    return initialDisplayText;
  }
}

export interface IGridOnForm {
  mainObject: ICoreDataEntity;
  objectType?: string | number;
  referencingObjectTypeColumn?: string;
  referencingIdColumn?: string;
  data: ICoreDataEntity & Object;
  abcGrid: _AbcGridComponent;
}
